import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, Share2, Lock, Clapperboard, Heart, Users, Play, MessageCircle, Send, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { getTheme, themeStyle } from "@/lib/eventTheme";
import { Logo } from "@/components/Common";

const fmtYear = (d) => (d ? new Date(d).getFullYear() : "");

export default function Watch() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | notfound | ready | private | not_ready
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [rx, setRx] = useState({ hearts: 0, notes: [] });
  const [hearted, setHearted] = useState(false);
  const [noteName, setNoteName] = useState("");
  const [noteMsg, setNoteMsg] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);

  const loadReactions = async () => {
    try { const r = await api.get(`/watch/${slug}/reactions`); setRx(r.data); } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/watch/${slug}`);
        setData(res.data);
        setStatus(res.data.state);
        if (res.data.state === "ready") loadReactions();
        try {
          const key = `gathervoices_watch_${slug}`;
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            api.post("/track", { name: "watch_open", slug, meta: { state: res.data.state } }).catch(() => {});
          }
        } catch {}
      } catch {
        setStatus("notfound");
      }
    })();
    // eslint-disable-next-line
  }, [slug]);

  const sendHeart = async () => {
    if (hearted) return;
    setHearted(true);
    setRx((r) => ({ ...r, hearts: (r.hearts || 0) + 1 }));
    try { await api.post(`/watch/${slug}/reactions`, { kind: "heart" }); }
    catch { setHearted(false); }
  };

  const sendNote = async (e) => {
    e.preventDefault();
    if (!noteMsg.trim()) return;
    setNoteBusy(true);
    try {
      await api.post(`/watch/${slug}/reactions`, { kind: "note", name: noteName, message: noteMsg });
      toast.success("Thank you — your note was sent to the family for approval.");
      setNoteMsg(""); setNoteName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your note");
    } finally { setNoteBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Watch link copied", { description: "Share it with anyone who couldn't be there." });
  };

  const share = async () => {
    const shareData = { title: `A tribute to ${data?.honoree_name}`, text: `Watch this tribute film for ${data?.honoree_name}.`, url: window.location.href };
    if (navigator.share) { try { await navigator.share(shareData); } catch {} }
    else copyLink();
  };

  if (status === "loading")
    return <div className="min-h-screen grid place-items-center"><div className="h-10 w-10 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;

  if (status === "notfound")
    return (
      <StateShell title="Film not found" line="This link may have expired or is incorrect.">
        <Link to="/" data-testid="watch-home-link" className="mt-4 inline-block font-semibold underline">Go to GatherVoices</Link>
      </StateShell>
    );

  const theme = getTheme(data?.type || "memorial");

  if (status === "private")
    return (
      <StateShell theme={theme} icon={<Lock size={22} className="text-white" />} title="This film is private"
        line={`The organizer hasn't opened the watch page for ${data?.honoree_name || "this tribute"} yet.`} />
    );

  if (status === "not_ready")
    return (
      <StateShell theme={theme} icon={<Clapperboard size={22} className="text-white" />} title="The film isn't ready yet"
        line={`We're still gathering tributes for ${data?.honoree_name || "this celebration"}. Please check back soon.`} />
    );

  // ready
  const streamUrl = fileUrl(data.film.file_id);
  const isMemorial = data.type === "memorial";

  return (
    <div className="min-h-screen" style={themeStyle(data.type)} data-testid="watch-page">
      {/* wash */}
      <div className="fixed inset-0 -z-10" style={{ backgroundImage: theme.heroWash }} />

      <header className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <Logo />
        <Link to="/" data-testid="watch-create-own-link"
          className="text-sm font-semibold px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition-colors">
          Create your own
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center pt-4 pb-8">
          <p className="text-sm font-semibold uppercase tracking-[0.2em]" style={{ color: `hsl(${theme.accent})` }}>{theme.framing}</p>
          <h1 data-testid="watch-honoree-name" className="mt-2 font-display text-4xl sm:text-5xl font-semibold tracking-[-0.02em]">{data.honoree_name}</h1>
          {isMemorial && (data.dates?.start || data.dates?.end) && (
            <p className="mt-2 text-[hsl(var(--muted-foreground))]">{fmtYear(data.dates?.start)} — {fmtYear(data.dates?.end)}</p>
          )}
          {!isMemorial && data.relationship && <p className="mt-1 text-[hsl(var(--muted-foreground))]">{data.relationship}</p>}
        </motion.div>

        {/* Player */}
        <motion.div initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative rounded-3xl overflow-hidden bg-black shadow-[0_30px_80px_rgba(20,16,12,0.28)] ring-1 ring-black/10">
          <video ref={videoRef} data-testid="watch-film-video" src={streamUrl} controls playsInline
            poster={data.photos?.[0] ? fileUrl(data.photos[0]) : undefined}
            className="w-full aspect-video bg-black" onPlay={() => setStarted(true)} />
          {!started && (
            <button data-testid="watch-play-overlay" onClick={() => videoRef.current?.play()}
              className="absolute inset-0 grid place-items-center bg-black/25 hover:bg-black/15 transition-colors group">
              <span className="h-20 w-20 grid place-items-center rounded-full shadow-2xl transition-transform group-hover:scale-105" style={{ background: `hsl(${theme.accent})` }}>
                <Play size={34} className="text-white translate-x-0.5" fill="currentColor" />
              </span>
            </button>
          )}
        </motion.div>

        {/* Meta + actions */}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p data-testid="watch-film-meta" className="text-sm text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
            <Clapperboard size={16} style={{ color: `hsl(${theme.accent})` }} />
            {data.film.clip_count} {data.film.clip_count === 1 ? "voice" : "voices"} · one tribute film
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button data-testid="watch-share-button" onClick={share}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] font-semibold hover:bg-[hsl(var(--muted))] transition-colors">
              <Share2 size={16} /> Share
            </button>
            <a data-testid="watch-download-button" href={streamUrl} download={`gathervoices-${data.slug}.mp4`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold hover:opacity-95 transition-opacity active:translate-y-[1px]"
              style={{ background: `hsl(${theme.accent})` }}>
              <Download size={16} /> Download film
            </a>
          </div>
        </div>

        {/* Story */}
        {data.story && (
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
            className="mt-10 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 sm:p-8">
            <Heart size={20} style={{ color: `hsl(${theme.accent})` }} />
            <p className="mt-3 leading-relaxed whitespace-pre-wrap">{data.story}</p>
          </motion.div>
        )}

        {/* Featuring */}
        {data.voices?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6 }}
            className="mt-8">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Users size={20} style={{ color: `hsl(${theme.accent})` }} /> With voices from</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {data.voices.map((v, i) => (
                <span key={i} data-testid={`watch-voice-${i}`} className="rounded-full px-4 py-2 text-sm font-medium" style={{ background: `hsl(${theme.chipBg})`, color: `hsl(${theme.accentInk})` }}>
                  {v.name}{v.relationship ? <span className="opacity-70"> · {v.relationship}</span> : null}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* Reactions */}
        <section className="mt-12" data-testid="watch-reactions">
          <div className="flex flex-col items-center text-center">
            <button data-testid="watch-heart-button" onClick={sendHeart} disabled={hearted}
              className="group inline-flex items-center gap-3 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-6 py-3.5 hover:shadow-md transition-shadow disabled:opacity-80">
              <Heart size={22} className={hearted ? "fill-current" : ""} style={{ color: `hsl(${theme.accent})` }} />
              <span className="font-semibold">{hearted ? "Thank you" : "Send love"}</span>
              <span data-testid="watch-heart-count" className="tabular-nums text-[hsl(var(--muted-foreground))]">{rx.hearts || 0}</span>
            </button>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {/* Leave a note */}
            <form onSubmit={sendNote} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="watch-note-form">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2"><MessageCircle size={18} style={{ color: `hsl(${theme.accent})` }} /> Leave a note</h3>
              <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Notes appear once the family approves them.</p>
              <input data-testid="watch-note-name" value={noteName} onChange={(e) => setNoteName(e.target.value)} placeholder="Your name"
                className="mt-3 w-full rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <textarea data-testid="watch-note-message" value={noteMsg} onChange={(e) => setNoteMsg(e.target.value)} rows={3} placeholder="Share a memory or a kind word…"
                className="mt-2 w-full rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <button data-testid="watch-note-submit" disabled={noteBusy || !noteMsg.trim()}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold disabled:opacity-60" style={{ background: `hsl(${theme.accent})` }}>
                {noteBusy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send note
              </button>
            </form>

            {/* Approved notes */}
            <div>
              <h3 className="font-display text-lg font-semibold">Notes from loved ones</h3>
              {(!rx.notes || rx.notes.length === 0) ? (
                <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Be the first to leave a note.</p>
              ) : (
                <div className="mt-3 space-y-3 max-h-80 overflow-auto pr-1">
                  {rx.notes.map((n, i) => (
                    <div key={i} data-testid={`watch-note-${i}`} className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4">
                      <p className="leading-relaxed text-sm">{n.message}</p>
                      <div className="mt-2 text-sm font-semibold" style={{ color: `hsl(${theme.accent})` }}>{n.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Create-your-own CTA */}
        <div className="mt-14 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 text-center">
          <p className="font-display text-2xl font-semibold">Create a tribute of your own</p>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto">Gather video messages from everyone who matters, and we'll weave them into one beautiful film — free to start.</p>
          <Link to="/" data-testid="watch-cta-create" className="mt-5 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity">
            <Heart size={16} /> Start with GatherVoices
          </Link>
        </div>
      </main>

      <footer className="border-t border-[hsl(var(--border))]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Logo />
          {data.branding?.name ? (
            <p data-testid="watch-branding" className="text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-2">
              {data.branding.logo_file_id && <img src={fileUrl(data.branding.logo_file_id)} alt="" className="h-6 w-6 rounded object-cover" />}
              Presented with care by <strong className="text-[hsl(var(--foreground))]">{data.branding.name}</strong>
            </p>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Made with care · support@gathervoices.io</p>
          )}
        </div>
      </footer>
    </div>
  );
}

function StateShell({ title, line, children, theme, icon }) {
  return (
    <div className="min-h-screen grid place-items-center px-4 text-center" style={theme ? themeStyle(undefined) : undefined}>
      {theme && <div className="fixed inset-0 -z-10" style={{ backgroundImage: theme.heroWash }} />}
      <div className="max-w-sm">
        {icon && <div className="h-14 w-14 mx-auto grid place-items-center rounded-full mb-4" style={{ background: `hsl(${theme?.accent || "35 38% 45%"})` }}>{icon}</div>}
        <h1 className="font-display text-3xl font-semibold">{title}</h1>
        <p className="mt-2 text-[hsl(var(--muted-foreground))]">{line}</p>
        {children}
      </div>
    </div>
  );
}
